import React, { FormEvent, useState } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { ArrowRightLeft, Loader2, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const SEARCH_DOCUMENT_TRANSFER_PARTICIPANTS = gql`
  query SearchDocumentTransferParticipants($search: String!) {
    searchParticipants(search: $search) {
      id
      nameHistory {
        firstName
        lastName
      }
      cid
    }
  }
`;

export const TRANSFER_PARTICIPANT_DOCUMENTS = gql`
  mutation TransferParticipantDocuments($odrnumber: Int!, $documentID: [String]) {
    transferParticipantDocuments(odrnumber: $odrnumber, documentID: $documentID)
  }
`;

export type TransferableDocument = {
  id: string | number;
  type?: string | null;
};

type ParticipantSearchResult = {
  id?: string | number | null;
  cid?: string | null;
  nameHistory?: Array<{
    firstName?: string | null;
    lastName?: string | null;
  }> | null;
};

export const buildTransferDocumentId = (document: TransferableDocument): string => {
  if (document.type === "LACARE_Initial_Assessment") return `i${document.id}`;
  if (document.type === "LACARE_Care_Plan") return `c${document.id}`;
  if (document.type === "LACAREProgram_Completion") return `p${document.id}`;
  return `d${document.id}`;
};

const participantName = (participant: ParticipantSearchResult): string => {
  const names = (participant.nameHistory || [])
    .map((name) => `${name.firstName || ""} ${name.lastName || ""}`.trim())
    .filter(Boolean);

  return Array.from(new Set(names)).join(", ") || "Name unavailable";
};

export const DocumentTransferDialog = ({
  currentOdrNumber,
  documents,
  onClose,
  onTransferred,
  open,
}: {
  currentOdrNumber: number;
  documents: TransferableDocument[];
  onClose: () => void;
  onTransferred: () => Promise<void> | void;
  open: boolean;
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchParticipants, { data, error, loading }] = useLazyQuery(
    SEARCH_DOCUMENT_TRANSFER_PARTICIPANTS,
    { fetchPolicy: "network-only" }
  );
  const [transferDocuments, { loading: transferring }] = useMutation(
    TRANSFER_PARTICIPANT_DOCUMENTS
  );

  const results: ParticipantSearchResult[] = data?.searchParticipants || [];

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const search = searchInput.trim();
    if (!search) return;
    setHasSearched(true);
    void searchParticipants({ variables: { search } });
  };

  const handleTransfer = async (participant: ParticipantSearchResult) => {
    const targetOdrNumber = Number(participant.id);
    if (!Number.isInteger(targetOdrNumber)) {
      toast.error("The selected participant does not have a valid ODR number.");
      return;
    }
    if (targetOdrNumber === currentOdrNumber) return;

    const confirmed = window.confirm(
      `Transfer ${documents.length} document${documents.length === 1 ? "" : "s"} to ODR ${targetOdrNumber}${participant.cid ? ` (CID ${participant.cid})` : ""}?`
    );
    if (!confirmed) return;

    try {
      await transferDocuments({
        variables: {
          odrnumber: targetOdrNumber,
          documentID: documents.map(buildTransferDocumentId),
        },
      });
      await onTransferred();
      toast.success(
        `${documents.length} document${documents.length === 1 ? "" : "s"} transferred.`
      );
      onClose();
    } catch (transferError: any) {
      toast.error(transferError?.message || "Unable to transfer documents.");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen: boolean) => {
        if (nextOpen) return;
        if (transferring) return;
        onClose();
      }}
    >
      <DialogContent
        className="sm:max-w-[900px] w-[90vw] h-[75vh] flex flex-col p-0 gap-0"
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <div className="flex-none border-b bg-white p-4">
          <DialogTitle>Transfer Documents</DialogTitle>
          <DialogDescription className="mt-1">
            Select the participant who should receive {documents.length} selected document{documents.length === 1 ? "" : "s"}.
          </DialogDescription>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4">
          <form className="flex items-center gap-2" onSubmit={handleSearch}>
            <label className="sr-only" htmlFor="participant-document-transfer-search">
              Search participants
            </label>
            <input
              id="participant-document-transfer-search"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by name, CID, MRUN, or FIN"
              className="h-9 min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-3 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            <Button
              id="participant-document-transfer-search-button"
              type="submit"
              size="sm"
              disabled={loading || !searchInput.trim()}
              className="gap-1.5"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </Button>
          </form>

          <div className="min-h-0 flex-1 overflow-auto rounded-md border border-gray-200">
            {error ? (
              <div className="p-6 text-center text-sm text-red-600">
                Unable to search participants.
              </div>
            ) : null}
            {!error && !loading && results.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                {hasSearched ? "No participants found." : "Search for the destination participant."}
              </div>
            ) : null}
            {!error && results.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Participant</TableHead>
                    <TableHead className="w-[150px]">ODR Number</TableHead>
                    <TableHead className="w-[150px]">CID</TableHead>
                    <TableHead className="w-[130px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((participant, index) => {
                    const targetOdrNumber = Number(participant.id);
                    const isCurrentParticipant = targetOdrNumber === currentOdrNumber;
                    const resultKey = `${participant.id || "participant"}-${index}`;
                    return (
                      <TableRow key={resultKey}>
                        <TableCell className="font-medium text-gray-900">
                          {participantName(participant)}
                        </TableCell>
                        <TableCell>{participant.id || "—"}</TableCell>
                        <TableCell>{participant.cid || "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            id={`participant-document-transfer-to-${participant.id}`}
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={transferring || isCurrentParticipant}
                            className="gap-1.5"
                            onClick={() => void handleTransfer(participant)}
                          >
                            {transferring ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ArrowRightLeft className="h-4 w-4" />
                            )}
                            {isCurrentParticipant ? "Current" : "Transfer"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : null}
          </div>
        </div>

        <div className="flex flex-none justify-end border-t bg-gray-50 p-4">
          <Button type="button" variant="outline" size="sm" disabled={transferring} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
